import MultiSelectDropdown from '@/app/(webapp)/_components/multi-select-dropdown';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import HoverTooltipText from '@/components/ui/hover-tooltip-text';
import { IconChevronDown, IconLoading } from '@/components/ui/icons';
import ToggleSwitch from '@/components/ui/toggle-switch';
import type {
    AccountBuyerObjectType,
    AccountSellerObjectType,
    Category,
    CountryIsoCode,
    Genre,
    Program as GraphQLProgram,
} from '@/graphql/graphql';
import { LanguageIsoCode } from '@/graphql/graphql';
import { isoEn } from '@/lib/utils';
import iconInfoWhite from '@public/icons/info.svg';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';

type ProgramStringField =
    | 'titles'
    | 'synopsis'
    | 'keywords'
    | 'directors'
    | 'casting'
    | 'awards'
    | 'imdb'
    | 'firstBroadcastDate'
    | 'productionCountryIsoCode'
    | 'category';
type MultiLangField =
    | 'titles'
    | 'synopsis'
    | 'keywords'
    | 'directors'
    | 'casting'
    | 'awards'
    | 'imdb'
    | 'firstBroadcastDate'
    | 'productionCountryIsoCode'
    | 'category';

interface ProgramMetadataProps {
    program: GraphQLProgram;
    user: AccountSellerObjectType | AccountBuyerObjectType | null;
    isTechnicalInfoActive?: boolean;
    isSeller: boolean;
    isAdmin: boolean;
    isBuyer: boolean;
    isInfoOpen: boolean;
    setIsInfoOpen: (open: boolean) => void;
    categories: Category[];
    genres: Genre[];
    selectedGenres: string[];
    setSelectedGenres: (genres: string[]) => void;
    countries: Array<{ value: CountryIsoCode; label: string }>;
    formInputClassName: string;
    t: (key: string, params?: Record<string, string | number>) => string;
    tc: (code: CountryIsoCode) => string;
    updateStringField: (fieldName: ProgramStringField, newValue: string | null) => void;
    updateArrayField: (fieldName: string, newValues: string[]) => void;
    updateMultiLanguageField: (
        fieldName: MultiLangField,
        newValue: string,
        localeToChange?: string,
        programToChange?: GraphQLProgram | null,
    ) => void;
    getMultiLanguageValue: (program: GraphQLProgram, fieldName: MultiLangField, customLang: string) => string;
    getProgramYear: (program: GraphQLProgram) => number | undefined;
    getProgramGenres: (program: GraphQLProgram) => string;
    getProgramKeywords: (program: GraphQLProgram, customLocale: string) => string | undefined;
    getProgramAudioLanguages: (program: GraphQLProgram) => string;
    currentLocaleFieldView: string;
    containerClassName: string;
    handleToggleChange: ({ isSegmentable, isExclusive }: { isSegmentable: boolean; isExclusive: boolean }) => void;
}

export default function Metadata({
    containerClassName,
    program,
    user,
    isTechnicalInfoActive,
    isAdmin,
    isBuyer,
    isSeller,
    isInfoOpen,
    setIsInfoOpen,
    categories,
    genres,
    selectedGenres,
    setSelectedGenres,
    countries,
    formInputClassName,
    updateStringField,
    updateArrayField,
    updateMultiLanguageField,
    getMultiLanguageValue,
    getProgramYear,
    getProgramGenres,
    getProgramKeywords,
    getProgramAudioLanguages,
    currentLocaleFieldView,
    handleToggleChange,
}: ProgramMetadataProps) {
    const [isTechnicalInfoOpen, setIsTechnicalInfoOpen] = React.useState<boolean>(false);
    const dtClassName = `text-[#C4C4C4] ${isSeller ? 'pt-[11px] pr-[.5rem]' : 'pr-[.5rem]'}`;
    const staticDtClassName = `${isSeller ? 'pt-[8px]' : ''} text-[#C4C4C4]`;
    const gridClassName = 'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)]';
    const staticRowClassName = 'grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)]';
    const tl = useTranslations('IsoLanguage');
    const sellerDdClassName = 'p-[8px]';
    const showPublicName = (!isSeller || isAdmin) && program.publicSeller?.legalName;
    const excludedGenresIDS = process.env.NEXT_PUBLIC_EXCLUDED_GENRE_IDS
        ? process.env.NEXT_PUBLIC_EXCLUDED_GENRE_IDS.replace(/\s+/g, '').split(',')
        : [];
    let displayLanguages: string;

    const tp = useTranslations('ProgramScreeningRoom');
    const tc = useTranslations('IsoCountry');

    const isValidYear = (value: string) => {
        if (!/^\d{4}$/.test(value)) return false;
        const year = Number(value);
        return year >= 1950 && year <= new Date().getFullYear();
    };

    if (isBuyer) {
        displayLanguages = tp('subtitleSpecificLanguage', {
            currentLanguage: tl((user as AccountBuyerObjectType).broadcastLanguage.toUpperCase() as LanguageIsoCode),
        });
    } else {
        displayLanguages = tp('subtitleAllLanguages');
    }

    return (
        <div className={containerClassName}>
            <div className={'bg-white/10 rounded-xl py-[40px] px-[30px] mb-8'}>
                <div className="flex justify-between lg:hidden">
                    <button
                        type="button"
                        onClick={() => {
                            if (!isInfoOpen) {
                                setIsInfoOpen(true);
                            } else {
                                setIsInfoOpen(false);
                            }
                        }}
                        className="font-bold w-full text-left flex justify-between"
                    >
                        {tp('moreInfoLabel')}
                        <IconChevronDown
                            className={`${isInfoOpen ? 'transform rotate-180' : ''} transition-all duration-700`}
                        />
                    </button>
                </div>
                <dl
                    className={`${isSeller ? 'space-y-2' : 'space-y-[20px]'} ${
                        isInfoOpen ? 'max-h-[1000px] mt-5' : 'max-h-0'
                    } overflow-hidden transition-all duration-700 ease-in-out lg:max-h-none lg:overflow-visible lg:block space-y-[6px] text-sm `}
                >
                    {showPublicName && (
                        <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''}`}>
                            <dt className={staticDtClassName}>{tp('metaData.sellerName')}</dt>
                            <dd className={isSeller ? sellerDdClassName : ''}>{program.publicSeller?.legalName}</dd>
                        </div>
                    )}
                    <div className={gridClassName}>
                        <dt className={dtClassName}>{tp('metaData.category')}</dt>
                        {isSeller ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <div className={`w-full ${formInputClassName}`}>
                                        {program.category?.title}
                                        <ChevronDown className="ml-2 w-6 text-white" />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="px-[10px] min-w-[240px] py-5" align="end">
                                    {categories.map((cat: Category) => (
                                        <DropdownMenuItem
                                            className={`
                                                cursor-pointer
                                                px-[10px] py-[5px]
                                                rounded-[4px]
                                                hover:bg-[rgba(255,255,255,0.30)]
                                                transition-all duration-300
                                                mb-[15px]
                                                last:mb-0
                                                ${cat.id === program.category.id ? 'bg-[rgba(255,255,255,0.15)]' : ''}
                                            `}
                                            key={cat.id}
                                            onClick={() => updateStringField('category', cat.id)}
                                            onKeyUp={() => updateStringField('category', cat.id)}
                                        >
                                            {cat.title ?? cat.title[isoEn]}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <dd className={isSeller ? sellerDdClassName : ''}>{program.category?.title}</dd>
                        )}
                    </div>

                    <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''}`}>
                        <dt className={staticDtClassName}>{tp('metaData.length')}</dt>
                        <dd className={isSeller ? sellerDdClassName : ''}>
                            {program.episodesQuantity && program.episodesQuantity > 0
                                ? tp('metaData.episodesLengthValue', {
                                      episodesQty: `${program.episodesQuantity}`,
                                      episodeDuration: (program.duration && program.episodesQuantity
                                          ? Math.round(program.duration / program.episodesQuantity)
                                          : 0
                                      ).toString(),
                                  })
                                : tp('metaData.programLengthValue', {
                                      programDuration: program.duration ? program.duration.toString() : '0',
                                  })}
                        </dd>
                    </div>
                    <div className={`${gridClassName} ${!getProgramYear(program) && !isSeller ? 'hidden' : ''}`}>
                        <dt className={dtClassName}>{tp('metaData.releaseYear')}</dt>
                        <dd>
                            {isSeller ? (
                                <input
                                    className={`w-full min-h-[42px] ${formInputClassName}`}
                                    defaultValue={getProgramYear(program)}
                                    onBlur={(e) => {
                                        const newValue = e.target.value.trim();
                                        const previousValue = getProgramYear(program)?.toString() ?? '';
                                        if (!isValidYear(newValue)) {
                                            e.target.value = previousValue;
                                            return;
                                        }
                                        updateStringField('firstBroadcastDate', newValue);
                                    }}
                                />
                            ) : (
                                getProgramYear(program)
                            )}
                        </dd>
                    </div>
                    {isSeller ? (
                        <div className={gridClassName}>
                            <dt className={dtClassName}>{tp('metaData.genres')}</dt>
                            <dd>
                                <MultiSelectDropdown
                                    options={genres.map((genre: Genre) => {
                                        return {
                                            label: genre.title,
                                            disabled:
                                                (selectedGenres.length === 1 && selectedGenres.includes(genre.id)) ||
                                                excludedGenresIDS.includes(genre.id),
                                            value: genre.id,
                                        };
                                    })}
                                    placeHolder={getProgramGenres(program) || tp('metaData.selectGenres')}
                                    customClass="cursor-pointer"
                                    styleClass={formInputClassName}
                                    selectedValues={selectedGenres}
                                    onValueChange={setSelectedGenres}
                                    onBlur={(values) => updateArrayField('genres', values)}
                                    minValue={1}
                                />
                            </dd>
                        </div>
                    ) : getProgramGenres(program) !== '' ? (
                        <div className={gridClassName}>
                            <dt className={dtClassName}>{tp('metaData.genres')}</dt>
                            <dd className={isSeller ? sellerDdClassName : ''}>{getProgramGenres(program)}</dd>
                        </div>
                    ) : undefined}
                    <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''}`}>
                        <dt className={staticDtClassName}>{tp('metaData.audio')}</dt>
                        <dd className={isSeller ? sellerDdClassName : ''}>{getProgramAudioLanguages(program)}</dd>
                    </div>
                    <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''}`}>
                        <dt className={staticDtClassName}>{tp('metaData.subtitles')}</dt>
                        <dd className={isSeller ? sellerDdClassName : ''}>
                            <HoverTooltipText text={displayLanguages} tooltipText={tp('subtitleTooltip')} />
                        </dd>
                    </div>
                    {program.episodesQuantity && program.episodesQuantity > 0 ? (
                        <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''}`}>
                            <dt className={staticDtClassName}>{tp('metaData.episodes')}</dt>
                            <dd className={isSeller ? sellerDdClassName : ''}>{program.episodesQuantity}</dd>
                        </div>
                    ) : undefined}
                    <div
                        className={`${gridClassName} ${!program.productionCountryIsoCode && !isSeller ? 'hidden' : ''}`}
                    >
                        <dt className={dtClassName}>{tp('metaData.mainProductionCountry')}</dt>
                        <dd>
                            {isSeller ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <div className={`w-full ${formInputClassName}`}>
                                            {program.productionCountryIsoCode?.toUpperCase()
                                                ? tc(program.productionCountryIsoCode.toUpperCase() as CountryIsoCode)
                                                : tp('metaData.selectMainProductionCountry')}
                                            <ChevronDown className="ml-2 w-6 text-white" />
                                        </div>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent className="px-[10px] py-5 " align="end">
                                        {countries.map((country) => (
                                            <DropdownMenuItem
                                                className={`cursor-pointer
                                                        px-[10px] py-[5px]
                                                        mb-[15px]
                                                        last:mb-0
                                                        rounded-[4px]
                                                        hover:bg-[rgba(255,255,255,0.30)]
                                                        transition-all duration-300
                                                        ${
                                                            program.productionCountryIsoCode &&
                                                            country.value.toLowerCase() ===
                                                                program.productionCountryIsoCode.toLowerCase()
                                                                ? 'bg-[rgba(255,255,255,0.15)]'
                                                                : ''
                                                        }`}
                                                key={country.value}
                                                onClick={() =>
                                                    updateStringField('productionCountryIsoCode', country.value)
                                                }
                                                onKeyUp={() =>
                                                    updateStringField('productionCountryIsoCode', country.value)
                                                }
                                            >
                                                {country.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : program.productionCountryIsoCode?.toUpperCase() ? (
                                tc(program.productionCountryIsoCode.toUpperCase() as CountryIsoCode)
                            ) : (
                                ''
                            )}
                        </dd>
                    </div>
                    <div className={`${gridClassName} ${!isSeller && !program.directors ? 'hidden' : ''}`}>
                        <dt className={dtClassName}>{tp('metaData.director')}</dt>
                        <dd>
                            {isSeller ? (
                                <textarea
                                    rows={2}
                                    defaultValue={program.directors ?? ''}
                                    className={`w-full ${formInputClassName}`}
                                    name="directorField"
                                    id="directorField"
                                    onBlur={(e) => {
                                        const newValue = e.target.value.trim();
                                        updateStringField('directors', newValue);
                                    }}
                                />
                            ) : (
                                program.directors
                            )}
                        </dd>
                    </div>
                    <div className={`${gridClassName} ${!isSeller && !program.casting ? 'hidden' : ''}`}>
                        <dt className={dtClassName}>{tp('metaData.casting')}</dt>
                        <dd>
                            {isSeller ? (
                                <textarea
                                    rows={3}
                                    defaultValue={program.casting ?? ''}
                                    className={`w-full ${formInputClassName}`}
                                    name="castingField"
                                    id="castingField"
                                    onBlur={(e) => {
                                        const newValue = e.target.value.trim();
                                        updateStringField('casting', newValue);
                                    }}
                                />
                            ) : (
                                program.casting
                            )}
                        </dd>
                    </div>
                    <div className={`${gridClassName} ${!isSeller && !program.awards ? 'hidden' : ''}`}>
                        <dt className={dtClassName}>{tp('metaData.award')}</dt>
                        <dd>
                            {isSeller ? (
                                <textarea
                                    rows={2}
                                    defaultValue={program.awards ?? ''}
                                    className={`w-full ${formInputClassName}`}
                                    name="awardsField"
                                    id="awardsField"
                                    onBlur={(e) => {
                                        const newValue = e.target.value.trim();
                                        updateStringField('awards', newValue);
                                    }}
                                />
                            ) : (
                                program.awards
                            )}
                        </dd>
                    </div>
                    <div
                        className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''} ${program.episodesQuantity && program.episodesQuantity > 0 ? 'hidden' : ''}`}
                    >
                        <dt className={staticDtClassName}>{tp('metaData.quality')}</dt>
                        <dd className={isSeller ? sellerDdClassName : ''}>{program.quality}</dd>
                    </div>
                    <div
                        className={`${gridClassName} ${!isSeller && !getProgramKeywords(program, currentLocaleFieldView) ? 'hidden' : ''}`}
                    >
                        <dt className={dtClassName}>{tp('metaData.keywords')}</dt>
                        <dd>
                            {isSeller ? (
                                <div>
                                    {Object.entries(program.keywords || {}).map(([lang]) => (
                                        <textarea
                                            key={lang}
                                            rows={3}
                                            defaultValue={getMultiLanguageValue(program, 'keywords', lang)}
                                            className={`w-full ${formInputClassName} ${currentLocaleFieldView !== lang ? 'hidden' : ''}`}
                                            name="keywordsField"
                                            id="keywordsField"
                                            onBlur={(e) => {
                                                const newValue = e.target.value.trim();

                                                const currentValue =
                                                    program.keywords?.[lang as LanguageIsoCode] ??
                                                    program.keywords?.[isoEn] ??
                                                    '';

                                                if (newValue === '') {
                                                    e.target.value = currentValue;
                                                    return;
                                                }

                                                updateMultiLanguageField('keywords', newValue, lang);
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : getProgramKeywords(program, currentLocaleFieldView) ? (
                                getProgramKeywords(program, currentLocaleFieldView)
                            ) : (
                                ''
                            )}
                        </dd>
                    </div>
                    <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''}`}>
                        <dt className={staticDtClassName}>{tp('metaData.originalLanguage')}</dt>
                        <dd className={isSeller ? sellerDdClassName : ''}>
                            {tl(program.audioLanguages.main.toUpperCase() as LanguageIsoCode)}
                        </dd>
                    </div>
                    {isSeller ? (
                        <div className={gridClassName}>
                            <dt className={dtClassName}>{tp('metaData.imdbLabel')}</dt>
                            <dd>
                                <input
                                    defaultValue={program.imdb ?? ''}
                                    className={`w-full min-h-[42px] ${formInputClassName}`}
                                    name="imdbField"
                                    id="imdbField"
                                    onBlur={(e) => {
                                        const newValue = e.target.value.trim();
                                        updateStringField('imdb', newValue);
                                    }}
                                />
                            </dd>
                        </div>
                    ) : program.imdb || program.creditRollUrl ? (
                        <div className={gridClassName}>
                            <dt className={dtClassName}>{tp('metaData.otherInformation')}</dt>
                            <dd
                                className={`${program.imdb ? 'flex justify-center items-center' : ''} ${isSeller ? sellerDdClassName : ''}`}
                            >
                                <ul className="self-start">
                                    {program.creditRollUrl ? (
                                        <li className={program.creditRollUrl ? 'pb-[5px]' : ''}>
                                            <a
                                                className="underline"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={program.creditRollUrl}
                                            >
                                                {tp('metaData.creditRollLabel')}
                                            </a>
                                        </li>
                                    ) : undefined}
                                    {program.imdb ? (
                                        <li>
                                            <a
                                                href={program.imdb}
                                                className="underline"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {tp('metaData.imdbLabel')}
                                            </a>
                                        </li>
                                    ) : undefined}
                                </ul>

                                {program.imdb ? (
                                    <Link href={program.imdb} className="w-[40px] mx-auto">
                                        <img src="/images/IMDb_logo.png" alt="IMDb logo" />
                                    </Link>
                                ) : undefined}
                            </dd>
                        </div>
                    ) : undefined}
                    <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''} mt-7`}>
                        <dt className={'text-[#C4C4C4]'}>{tp('metaData.exclusivityRight.title')}</dt>
                        <dd>
                            {isSeller ? (
                                <ToggleSwitch
                                    label={tp('metaData.exclusivityRight.seller.allow')}
                                    checked={program.isExclusive}
                                    onCheckedChange={(checked) =>
                                        handleToggleChange({
                                            isExclusive: checked,
                                            isSegmentable: program.isSegmentable,
                                        })
                                    }
                                    tooltipText={tp('metaData.exclusivityRight.tooltip')}
                                    customLabelClass={'flex-1'}
                                />
                            ) : (
                                <>
                                    {program.isExclusive
                                        ? tp('metaData.exclusivityRight.buyer.available')
                                        : tp('metaData.exclusivityRight.buyer.notAvailable')}
                                </>
                            )}
                        </dd>
                    </div>
                    <div className={`${staticRowClassName} ${isSeller ? 'min-h-[42px]' : ''} mt-7`}>
                        <dt className={'text-[#C4C4C4]'}>{tp('metaData.segmentRight.title')}</dt>
                        <dd>
                            {isSeller ? (
                                <ToggleSwitch
                                    label={tp('metaData.segmentRight.seller.allow')}
                                    checked={program.isSegmentable}
                                    onCheckedChange={(checked) =>
                                        handleToggleChange({ isSegmentable: checked, isExclusive: program.isExclusive })
                                    }
                                    tooltipText={tp('metaData.segmentRight.tooltip')}
                                    customLabelClass={'flex-1'}
                                />
                            ) : (
                                <>
                                    {program.isSegmentable
                                        ? tp('metaData.segmentRight.buyer.available')
                                        : tp('metaData.segmentRight.buyer.notAvailable')}
                                </>
                            )}
                        </dd>
                    </div>
                    {!program.creditRollUrl && !isSeller ? (
                        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                            <div className="col-span-full text-[#C4C4C4] pr-[.5rem]">
                                <p className="text-[14px] text-[#C4C4C4] text-center pt-[10px]">
                                    {tp('metaData.creditRollInfo')}
                                </p>
                            </div>
                        </div>
                    ) : undefined}
                </dl>
            </div>
            {isTechnicalInfoActive && (
                <div className={'bg-white/10 rounded-xl py-[40px] px-[30px] text-center'}>
                    <div className="flex justify-between lg:hidden">
                        <button
                            type="button"
                            onClick={() => setIsTechnicalInfoOpen(!isTechnicalInfoOpen)}
                            className="font-bold w-full text-left flex justify-between"
                        >
                            {tp('metaData.technicalInfo.title')}
                            <IconChevronDown
                                className={`${isTechnicalInfoOpen ? 'transform rotate-180' : ''} transition-all duration-700`}
                            />
                        </button>
                    </div>
                    <span className="text-[18px] font-[700] text-center hidden lg:block">
                        {tp('metaData.technicalInfo.title')}
                    </span>
                    <div
                        className={`${isTechnicalInfoOpen ? 'max-h-[500px]' : 'max-h-0'} overflow-hidden transition-all duration-700 ease-in-out lg:max-h-none lg:overflow-visible lg:block space-y-[6px] text-sm `}
                    >
                        <div
                            className={
                                isBuyer ? 'my-[20px] text-left lg:text-center' : 'mt-[20px] text-left lg:text-center'
                            }
                        >
                            <span className="text-[14px] text-[#fff]">{tp('metaData.technicalInfo.iconInfo')}</span>
                            <Image
                                src={iconInfoWhite}
                                alt="info icon"
                                className="inline-block w-[24px] h-[24px] mx-[8px]"
                            />
                            <span className="text-[14px] text-[#fff]">{tp('metaData.technicalInfo.iconInfoEnd')}</span>
                        </div>
                        {isBuyer ? (
                            <p className="text-[14px] text-[#C4C4C4] text-left lg:text-center">
                                {tp('metaData.technicalInfo.warning')}
                            </p>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
}
